import { useMemo } from "react"
import { useTranslation } from "providers/i18n"

import {
  Typography,
  CardMedia,
  CardContent,
  Box,
  Stack,
  Card,
  useTheme,
  Chip,
  SxProps,
} from "@mui/material"
import { deepmerge } from "@mui/utils"
import clsx from "clsx"

import {
  getFeatureDetail,
  IPromotionDetail,
  StatusByDateRange,
} from "utils/feature"
import { getDurationDate } from "utils/date"
import { getIcon } from "utils/icon"

import { LocationIcon } from "components/icons"
import { LocationInfo } from "components/domains/features/LocationInfo"

import { IFeature } from "interfaces"

interface IPromotionCardProps {
  promotion: IFeature
  dense?: boolean
  sx?: SxProps
}

export const PromotionCard = ({
  promotion,
  dense = false,
  sx = {},
}: IPromotionCardProps) => {
  const { t } = useTranslation()
  const {
    name,
    coverImage,
    venueLogo,
    startDate,
    endDate,
    hasLocation,
    isAd,
    status,
  } = getFeatureDetail(promotion) as IPromotionDetail

  const durationText = useMemo(
    () => getDurationDate(startDate, endDate),
    [startDate, endDate]
  )

  const theme = useTheme()
  const { styleOverrides } = theme.components?.[`PromotionCard`] || {}
  const {
    root,
    cardMedia,
    cardContent,
    venueLogoContainer,
    venueLogo: venueLogoStyle,
    cardContentStack,
    duration,
    cardTitle,
    cardInfoContainer,
    iconContainer,
    durationContainer,
    recommendText,
    recommendTextContainer,
    chipContainer,
    highlightTextChip,
    statusTextContainer,
    statusText,
  } = styleOverrides

  const locationIcon = useMemo(
    () =>
      getIcon(theme?.assets?.locationIcon) || (
        <LocationIcon className="PromotionCard-locationInfoIcon" />
      ),
    [theme]
  )

  return (
    <Card
      sx={deepmerge(root, sx)}
      className={clsx("PromotionCard-root", { "MuiCardContent-dense": dense })}
    >
      <CardMedia
        sx={cardMedia}
        image={coverImage}
        className="PromotionCard-cardMedia"
      >
        {isAd && (
          <>
            <Box sx={chipContainer}>
              <Chip
                label={t("Highlight")}
                size="small"
                sx={highlightTextChip}
              />
            </Box>
            <Box sx={recommendTextContainer}>
              <Typography variant="caption" component="p" sx={recommendText}>
                {t("Recommend")}
              </Typography>
            </Box>
          </>
        )}
        {status === StatusByDateRange.Upcoming && (
          <Box sx={statusTextContainer}>
            <Typography variant="caption" component="p" sx={statusText}>
              {t("Upcoming")}
            </Typography>
          </Box>
        )}
      </CardMedia>
      <CardContent sx={cardContent} className="PromotionCard-cardContent">
        <Stack spacing={1} sx={cardContentStack}>
          {venueLogo && (
            <Box sx={venueLogoContainer}>
              <CardMedia
                component="img"
                sx={venueLogoStyle}
                className="PromotionCard-venueLogo"
                image={venueLogo}
              />
            </Box>
          )}
          <Box sx={durationContainer}>
            <Typography
              className="PromotionCard-durationText"
              sx={duration}
              variant="subTitle"
              component="p"
            >
              {durationText}
            </Typography>
          </Box>
          <Typography
            className="PromotionCard-cardTitleText"
            sx={cardTitle}
            variant="title"
            component="h6"
          >
            {t(name)}
          </Typography>
        </Stack>
        <Stack spacing={1}>
          <Box
            sx={{
              ...cardInfoContainer,
              visibility: hasLocation ? "visible" : "hidden",
            }}
          >
            <Box sx={iconContainer}>{locationIcon}</Box>
            <LocationInfo
              feature={promotion}
              variant="subTitle"
              component="p"
              className="PromotionCard-locationInfo"
            />
          </Box>
        </Stack>
      </CardContent>
    </Card>
  )
}
